import { NgClass } from "@angular/common";
import {
  Component,
  DestroyRef,
  Input,
  OnInit,
  ViewChild,
  inject,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { animate, style, transition, trigger } from "@angular/animations";
import { MenuDirective, MenuModule } from "headlessui-angular";
import { Store } from "@ngrx/store";
import { distinctUntilChanged, map, merge, of, skip } from "rxjs";
import { switchMap } from "rxjs/operators";
import { AuthService } from "../../../core/services/auth/auth.service";
import { ToastService } from "../../../core/services/toast/toast.service";
import {
  resolveWorkspaceBrandingFromProfile,
  resolveWorkspaceLogoDisplayUrl,
} from "../../../modules/settings/profile/profile-me.mapper";
import type {
  ProfileMeResponse,
  ProfileWorkspaceSummary,
} from "../../../modules/settings/profile/profile.types";
import { environment } from "../../../../environments/environment";
import { initialsFromDisplayName } from "../../utils/display-name-initials.util";

export interface WorkspaceSwitcherOption {
  id: string;
  displayName: string;
  logoUrl: string | null;
  logoInitial: string;
}

@Component({
  selector: "app-workspace-switcher",
  standalone: true,
  templateUrl: "./workspace-switcher.component.html",
  host: {
    class: "block min-w-0 flex-1",
  },
  animations: [
    trigger("toggleAnimation", [
      transition(":enter", [
        style({ opacity: 0, transform: "scale(0.95)" }),
        animate("100ms ease-out", style({ opacity: 1, transform: "scale(1)" })),
      ]),
      transition(":leave", [
        animate("75ms", style({ opacity: 0, transform: "scale(0.95)" })),
      ]),
    ]),
  ],
  imports: [NgClass, MenuModule],
})
export class WorkspaceSwitcherComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly auth = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly storeData = inject(
    Store<{ index: { sidebar: boolean; menu: string } }>,
  );

  @ViewChild(MenuDirective) private menu?: MenuDirective;

  /** Extra classes on the trigger button (sidebar vs header spacing). */
  @Input() triggerClass = "";
  /** Extra classes on the dropdown panel. */
  @Input() menuClass = "";

  workspaceName = environment.appName;
  workspaceLogoUrl: string | null = null;
  workspaceLogoInitial = "W";
  workspaceLogoLoadFailed = false;
  currentWorkspaceId = "";
  workspaces: WorkspaceSwitcherOption[] = [];
  switchingWorkspaceId: string | null = null;
  private logoLoadFailedIds = new Set<string>();

  ngOnInit(): void {
    merge(
      of(undefined).pipe(switchMap(() => this.auth.getProfileMe())),
      this.auth.profileMePushed$,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (me) => this.applyProfile(me),
        error: () => {
          /* keep defaults */
        },
      });

    // Close the open panel when the sidebar collapses (mini rail / slides away).
    this.storeData
      .select((d) => d.index)
      .pipe(
        map((state) => this.isSidebarCollapsed(state)),
        distinctUntilChanged(),
        skip(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((collapsed) => {
        if (collapsed) {
          this.closeMenu();
        }
      });
  }

  /** Close the Headless UI menu if it is open (used on sidebar collapse / mouseleave). */
  closeMenu(): void {
    if (this.menu?.expanded) {
      this.menu.toggle(null, false);
    }
  }

  get showWorkspaceLogoImage(): boolean {
    return !!this.workspaceLogoUrl?.trim() && !this.workspaceLogoLoadFailed;
  }

  get canSwitchWorkspace(): boolean {
    return this.workspaces.length > 1;
  }

  onWorkspaceLogoError(): void {
    this.workspaceLogoLoadFailed = true;
  }

  showOptionLogo(workspace: WorkspaceSwitcherOption): boolean {
    return (
      !!workspace.logoUrl?.trim() && !this.logoLoadFailedIds.has(workspace.id)
    );
  }

  onOptionLogoError(workspaceId: string): void {
    this.logoLoadFailedIds.add(workspaceId);
  }

  isCurrentWorkspace(workspace: WorkspaceSwitcherOption): boolean {
    return workspace.id === this.currentWorkspaceId;
  }

  switchWorkspace(workspace: WorkspaceSwitcherOption): void {
    if (
      !workspace.id ||
      workspace.id === this.currentWorkspaceId ||
      this.switchingWorkspaceId
    ) {
      return;
    }
    this.switchingWorkspaceId = workspace.id;
    this.closeMenu();
    this.auth.switchWorkspaceTo(workspace.id).subscribe({
      error: () => {
        this.switchingWorkspaceId = null;
        this.toast.error("Could not switch workspace. Please try again.");
      },
    });
  }

  /**
   * collapsible-vertical: mini rail when `sidebar` is false.
   * vertical: desktop sidebar hidden when `sidebar` is true.
   */
  private isSidebarCollapsed(state: {
    sidebar?: boolean;
    menu?: string;
  }): boolean {
    const menu = String(state?.menu ?? "");
    if (menu === "collapsible-vertical") {
      return state.sidebar !== true;
    }
    if (menu === "vertical") {
      return state.sidebar === true;
    }
    return false;
  }

  private applyProfile(me: ProfileMeResponse): void {
    const branding = resolveWorkspaceBrandingFromProfile(
      me,
      environment.appName,
    );
    this.workspaceName = branding.name;
    this.workspaceLogoUrl = branding.logoUrl;
    this.workspaceLogoInitial = branding.logoInitial;
    this.workspaceLogoLoadFailed = false;
    this.currentWorkspaceId = String(me.currentWorkspace?.id ?? "").trim();
    this.workspaces = this.mapWorkspacesFromProfile(me);
  }

  private mapWorkspacesFromProfile(
    me: ProfileMeResponse,
  ): WorkspaceSwitcherOption[] {
    const byId = new Map<string, WorkspaceSwitcherOption>();

    const add = (ws: ProfileWorkspaceSummary | null | undefined) => {
      const id = String(ws?.id ?? "").trim();
      if (!id || byId.has(id)) {
        return;
      }
      const displayName =
        String(ws?.displayName ?? "").trim() || environment.appName;
      byId.set(id, {
        id,
        displayName,
        logoUrl: resolveWorkspaceLogoDisplayUrl(ws?.logo),
        logoInitial: initialsFromDisplayName(displayName),
      });
    };

    add(me.currentWorkspace);
    for (const membership of me.workspaces ?? []) {
      add(membership?.workspace);
    }

    return Array.from(byId.values()).sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    );
  }
}
